import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { listTypeApi } from '../../entities/listType/listTypeApi';
import type { ListTypeDefinition, ListTypeEntry } from '../../entities/listType/listTypeTypes';
import { useAppSelector } from '../../store/hooks';
import { extractError } from '../../store/thunkHelpers';
import { useToast } from '../useToast';
import { ChevronDownGlyph, PlusGlyph } from '../icons';
import ListTypeEntryDialog from './ListTypeEntryDialog';

const BASE_PATH = '/settings/gestione-picklist';

/**
 * GESTIONE PICKLIST — le picklist del CRM, cioè quelle delle dotazioni dei moduli abilitati (FILO-16).
 * Due pagine: l'elenco delle picklist (`/settings/gestione-picklist`) e le voci di una
 * (`/settings/gestione-picklist/:listTypeDefinitionId`), con la freccia per tornare all'elenco. Una
 * rotta vera e non uno stato: il tasto indietro del browser e il refresh restano dove ci si aspetta.
 *
 * Il CRM Administrator cambia il testo delle voci (clic sulla riga, modale), ne aggiunge e le
 * disattiva. La key non cambia mai: il codice la confronta. Disattivare non chiede conferma: si
 * annulla con lo stesso clic, e la voce resta per chi la usa.
 *
 * Una riga bassa per voce (stili `picklist-settings__*`): le voci sono tante e si scorrono.
 */
export function PicklistSettings() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { listTypeDefinitionId } = useParams();
  const isAdmin = useAppSelector((state) => state.auth.user?.administrator ?? false);
  const [listTypeDefinitions, setListTypeDefinitions] = useState<ListTypeDefinition[] | null>(null);
  const [modale, setModale] = useState<{ listTypeDefinition: ListTypeDefinition; listTypeEntry?: ListTypeEntry } | null>(null);

  const carica = useCallback(
    () =>
      listTypeApi
        .list()
        .then(setListTypeDefinitions)
        .catch((e) => toast({ severity: 'error', message: extractError(e) })),
    [toast],
  );

  useEffect(() => {
    void carica();
  }, [carica]);

  const aggiorna = async (item: ListTypeEntry, payload: Parameters<typeof listTypeApi.update>[1]) => {
    try {
      await listTypeApi.update(item.listTypeEntryId, payload);
      await carica();
    } catch (e) {
      toast({ severity: 'error', message: extractError(e) });
    }
  };

  const attive = (listTypeDefinition: ListTypeDefinition) =>
    listTypeDefinition.listTypeEntries.filter((i) => i.active).length;

  if (listTypeDefinitions === null) return null;

  const listTypeDefinition = listTypeDefinitionId
    ? listTypeDefinitions.find((d) => String(d.listTypeDefinitionId) === listTypeDefinitionId)
    : undefined;

  // Un id che non c'è (picklist di un modulo spento, link vecchio): si torna all'elenco.
  if (listTypeDefinitionId && !listTypeDefinition) return <Navigate to={BASE_PATH} replace />;

  if (!listTypeDefinition) {
    return (
      <div className="picklist-settings">
        <div className="page-head">
          <div>
            <h1 className="page-title">{t('settings.picklists.title')}</h1>
            <p className="text-muted">{t('settings.picklists.description')}</p>
          </div>
        </div>

        <ul className="picklist-settings__list">
          {listTypeDefinitions.map((d) => (
            <li key={d.listTypeDefinitionId}>
              <button
                type="button"
                className="picklist-settings__picklist-row"
                onClick={() => navigate(`${BASE_PATH}/${d.listTypeDefinitionId}`)}
              >
                <span className="picklist-settings__row-label">{d.name}</span>
                <span className="relation-count">{attive(d)}</span>
                <ChevronDownGlyph className="picklist-settings__chevron" />
              </button>
            </li>
          ))}
        </ul>

        {listTypeDefinitions.length === 0 && <p className="text-muted">{t('settings.picklists.empty')}</p>}
      </div>
    );
  }

  const modificabile = isAdmin;

  return (
    <div className="picklist-settings">
      <Link to={BASE_PATH} className="text-sm text-primary detail-back-link">
        &larr; {t('settings.picklists.title')}
      </Link>

      <div className="picklist-settings__head">
        <h1 className="page-title">{listTypeDefinition.name}</h1>
        <span className="relation-count">{attive(listTypeDefinition)}</span>
        {modificabile && (
          <button type="button" className="btn-primary picklist-settings__add" onClick={() => setModale({ listTypeDefinition })}>
            <PlusGlyph /> {t('settings.picklists.newItem')}
          </button>
        )}
      </div>

      <ul className="picklist-settings__list">
        {listTypeDefinition.listTypeEntries.map((item) => (
          <li
            key={item.listTypeEntryId}
            className={`picklist-settings__row${item.active ? '' : ' picklist-settings__row--inactive'}${modificabile ? ' picklist-settings__row--clickable' : ''}`}
            onClick={modificabile ? () => setModale({ listTypeDefinition, listTypeEntry: item }) : undefined}
            onKeyDown={
              modificabile
                ? (e) => {
                    if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      setModale({ listTypeDefinition, listTypeEntry: item });
                    }
                  }
                : undefined
            }
            role={modificabile ? 'button' : undefined}
            tabIndex={modificabile ? 0 : undefined}
            aria-label={modificabile ? `${t('settings.picklists.edit')}: ${item.name}` : undefined}
          >
            <span className="picklist-settings__row-label" title={item.name}>
              {item.name}
            </span>
            <span className="picklist-settings__key" title={item.key}>
              {item.key}
            </span>
            <span>
              {!item.active && <span className="chip neutral">{t('settings.picklists.inactive')}</span>}
            </span>
            <span className="picklist-settings__actions">
              {modificabile && (
                <Tooltip title={t(item.active ? 'settings.picklists.deactivate' : 'settings.picklists.activate')}>
                  <IconButton
                    size="small"
                    aria-label={t(item.active ? 'settings.picklists.deactivate' : 'settings.picklists.activate')}
                    onClick={(e) => {
                      // Il clic sull'occhio non deve aprire anche la modale della riga.
                      e.stopPropagation();
                      void aggiorna(item, { active: !item.active });
                    }}
                  >
                    {item.active ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </Tooltip>
              )}
            </span>
          </li>
        ))}
      </ul>

      {modale && (
        <ListTypeEntryDialog
          listTypeDefinition={modale.listTypeDefinition}
          listTypeEntry={modale.listTypeEntry}
          onClose={() => setModale(null)}
          onSaved={() => void carica()}
        />
      )}
    </div>
  );
}
